import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { Cpu, Cloud, Database, Workflow, Code, Zap, Box, Network } from 'lucide-react';

const Technology = () => {
  const { isDark } = useTheme();

  const techStacks = [
    {
      icon: <Workflow className="w-12 h-12" />,
      title: 'Microsoft Power Platform',
      technologies: ['Power Apps', 'Power Automate', 'Power BI', 'Power Pages', 'Copilot Studio', 'Dataverse', 'AI Builder'],
      color: '#00AA66',
      bgPattern: 'M20 0L40 20L20 40L0 20Z',
    },
    {
      icon: <Database className="w-12 h-12" />,
      title: 'Azure Data Services',
      technologies: ['Azure Databricks', 'Synapse Analytics', 'Data Factory', 'Data Lake', 'Azure SQL', 'Cosmos DB', 'Stream Analytics'],
      color: '#0EA5E9',
      bgPattern: 'M0 0h40v40H0z',
    },
    {
      icon: <Cloud className="w-12 h-12" />,
      title: 'Azure Cloud Services',
      technologies: ['Azure Functions', 'Logic Apps', 'API Management', 'App Services', 'Azure DevOps', 'Key Vault', 'Azure AD'],
      color: '#14B8A6',
      bgPattern: 'M20 0L40 40H0Z',
    },
    {
      icon: <Code className="w-12 h-12" />,
      title: 'Development Frameworks',
      technologies: ['.NET Core', 'ASP.NET', 'React', 'Angular', 'Node.js', 'Python', 'TypeScript', 'C#'],
      color: '#10B981',
      bgPattern: 'M0 20L20 0L40 20L20 40Z',
    },
    {
      icon: <Network className="w-12 h-12" />,
      title: 'Microsoft 365',
      technologies: ['SharePoint Online', 'Microsoft Teams', 'Outlook', 'OneDrive', 'Microsoft Graph API', 'Dynamics 365'],
      color: '#00AA66',
      bgPattern: 'M10 0L30 20L10 40L0 30V10Z',
    },
    {
      icon: <Box className="w-12 h-12" />,
      title: 'Integration & AI',
      technologies: ['Xero', 'NetSuite', 'Azure Machine Learning', 'Cognitive Services', 'Azure OpenAI', 'MLflow'],
      color: '#0EA5E9',
      bgPattern: 'M20 10L30 20L20 30L10 20Z M0 0h10v10H0z M30 0h10v10H30z M0 30h10v10H0z M30 30h10v10H30z',
    },
  ];

  const certifications = [
    { name: 'Microsoft Certified', count: '15+' },
    { name: 'Azure Solutions', count: '10+' },
    { name: 'Power Platform', count: '20+' },
    { name: 'Integration Projects', count: '100+' },
  ];

  return (
    <div className='min-h-screen pt-20' data-testid='technology-page' style={{ background: isDark ? '#212529' : '#E8EDF2' }}>
      {/* Hero Section */}
      <section className='py-24 relative overflow-hidden'>
        {/* Animated Background Elements */}
        <div className='absolute inset-0 overflow-hidden pointer-events-none'>
          <motion.div
            className='absolute top-20 left-10 opacity-10'
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          >
            <svg width='120' height='120' viewBox='0 0 120 120'>
              <path d='M60 10L110 60L60 110L10 60Z' fill='#00AA66' />
            </svg>
          </motion.div>
          <motion.div
            className='absolute bottom-20 right-20 opacity-10'
            animate={{ rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          >
            <svg width='100' height='100' viewBox='0 0 100 100'>
              <circle cx='50' cy='50' r='40' fill='none' stroke='#14B8A6' strokeWidth='8' />
              <circle cx='50' cy='50' r='20' fill='#14B8A6' />
            </svg>
          </motion.div>
        </div>

        <div className='max-w-7xl mx-auto px-6 lg:px-12 relative z-10'>
          <motion.div
            className='text-center mb-16'
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 ${
                isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'
              }`}
              style={{ background: isDark ? '#212529' : '#E8EDF2' }}
            >
              <Cpu className='w-5 h-5 text-brand-primary' />
              <span className='text-brand-primary font-semibold text-sm'>Enterprise-Grade Stack</span>
            </motion.div>

            <h1 className={`text-5xl lg:text-7xl font-bold font-manrope mb-6 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Technology <span className='text-brand-primary'>Stack</span>
            </h1>
            <p className={`text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
              Industry-leading technologies powering robust, scalable, and future-proof integration solutions.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            className='grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                className={`p-6 rounded-2xl text-center ${
                  isDark ? 'shadow-neu-dark' : 'shadow-neu-light'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className='text-4xl font-bold font-manrope text-brand-primary mb-2'>{cert.count}</div>
                <div className={`text-sm font-medium ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
                  {cert.name}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Cards */}
      <section className='pb-24'>
        <div className='max-w-7xl mx-auto px-6 lg:px-12'>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {techStacks.map((stack, index) => (
              <motion.div
                key={index}
                className={`relative p-8 rounded-3xl transition-all duration-500 group overflow-hidden ${
                  isDark ? 'shadow-neu-dark hover:shadow-neu-dark-hover' : 'shadow-neu-light hover:shadow-neu-light-hover'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                {/* Animated Background Pattern */}
                <motion.div
                  className='absolute top-0 right-0 w-32 h-32 opacity-5'
                  animate={{ 
                    rotate: [0, 360],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 10 + index * 2, 
                    repeat: Infinity, 
                    ease: 'linear' 
                  }}
                >
                  <svg width='128' height='128' viewBox='0 0 40 40'>
                    <path d={stack.bgPattern} fill={stack.color} />
                  </svg>
                </motion.div>

                {/* Icon */}
                <motion.div
                  className={`inline-flex p-4 rounded-2xl mb-6 relative z-10 ${
                    isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'
                  }`}
                  style={{ 
                    background: isDark ? '#212529' : '#E8EDF2',
                    color: stack.color 
                  }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  {stack.icon}
                </motion.div>

                {/* Title */}
                <h3 
                  className={`text-xl font-bold font-manrope mb-4 relative z-10 ${
                    isDark ? 'text-dark-text' : 'text-light-text'
                  }`}
                  style={{ color: stack.color }}
                >
                  {stack.title}
                </h3>

                {/* Technologies */}
                <div className='flex flex-wrap gap-2 relative z-10'>
                  {stack.technologies.map((tech, techIndex) => (
                    <motion.span
                      key={techIndex}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                        isDark 
                          ? 'bg-white/5 text-dark-text-secondary' 
                          : 'bg-black/5 text-light-text-secondary'
                      }`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.05 * techIndex }}
                      whileHover={{ 
                        scale: 1.05,
                        backgroundColor: stack.color + '20',
                        color: stack.color
                      }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Visual */}
      <section className='pb-24'>
        <div className='max-w-7xl mx-auto px-6 lg:px-12'>
          <motion.div
            className={`p-12 lg:p-16 rounded-3xl relative overflow-hidden ${
              isDark ? 'shadow-neu-dark' : 'shadow-neu-light'
            }`}
            style={{ background: isDark ? '#212529' : '#E8EDF2' }}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className='text-center mb-12 relative z-10'>
              <h2 className={`text-3xl lg:text-4xl font-bold font-manrope mb-4 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
                Seamless <span className='text-brand-primary'>Integration</span> Ecosystem
              </h2>
              <p className={`text-lg ${isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}`}>
                Connecting your entire business technology stack
              </p>
            </div>

            {/* Integration Diagram */}
            <div className='relative h-64 flex items-center justify-center'>
              {/* Center Hub */}
              <motion.div
                className={`absolute w-24 h-24 rounded-full flex items-center justify-center ${
                  isDark ? 'shadow-neu-dark' : 'shadow-neu-light'
                }`}
                style={{ background: isDark ? '#212529' : '#E8EDF2' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Zap className='w-12 h-12 text-brand-primary' />
              </motion.div>

              {/* Orbiting Elements */}
              {['Power Apps', 'Xero', 'NetSuite', 'Azure', 'Custom API', 'Power BI'].map((item, index) => {
                const angle = (index * 60) * (Math.PI / 180);
                const radius = 120;
                const x = Math.cos(angle) * radius;
                const y = Math.sin(angle) * radius;

                return (
                  <motion.div
                    key={index}
                    className={`absolute px-4 py-2 rounded-xl text-sm font-semibold ${
                      isDark ? 'shadow-neu-dark-sm' : 'shadow-neu-light-sm'
                    }`}
                    style={{ 
                      background: isDark ? '#212529' : '#E8EDF2',
                      left: '50%',
                      top: '50%',
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
                    }}
                    animate={{ 
                      rotate: 360,
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ 
                      rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
                      scale: { duration: 2, repeat: Infinity, delay: index * 0.3 }
                    }}
                  >
                    <span className='text-brand-primary'>{item}</span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Technology;
